'use client'

import { ClientSelect } from '@repo/ui/ClientSelect'
import { CloseFilledIcon } from '@repo/ui/CloseFilledIcon'
import { CopyOutlineIcon } from '@repo/ui/CopyOutlineIcon'
import { NumericFormat } from 'react-number-format'
import { PlaidBankAccountSelect } from '@repo/ui/PlaidBankAccountSelect'
import { Portal } from '@mui/base/Portal'
import { UnpaidBillsSelect } from '@repo/ui/UnpaidBillsSelect'
import { useBankFeedsStore } from '@repo/store/useBankFeedsStore'
import { useEffect, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import MUIBackdrop from '@mui/material/Backdrop'
import MUIButton from '@mui/material/Button'
import MUIInputAdornment from '@mui/material/InputAdornment'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITextField from '@mui/material/TextField'
import MUITypography from '@mui/material/Typography'

export const BankFeedsCreatePaymentLink = ({
  bankFeed,
  isOpen,
  setOpen,
  testData = false
}: BankFeedsCreatePaymentLinkProps) => {
  const [amount, setAmount] = useState<number | null>(null)
  const [client, setClient] = useState<any>(null)
  const [copied, setCopied] = useState<boolean>(false)
  const [currencySymbol, setCurrencySymbol] = useState<string>('£')
  const [paymentLink, setPaymentLink] = useState<string | null>(null)
  const [plaidBankAccount, setPlaidBankAccount] = useState<any>(null)
  const [reference, setReference] = useState<string>('')
  const [saveDisabled, setSaveDisabled] = useState(true)
  const [unpaidBills, setUnpaidBills] = useState<any>(null)

  const { createPaymentLink } = useBankFeedsStore()

  const theme = useTheme()

  const MIN_AMOUNT = 1

  const clampAmount = (v: number | null) => {
    if (v == null || Number.isNaN(v)) return null
    const rounded = Math.round(v * 100) / 100
    return Math.max(MIN_AMOUNT, rounded)
  }

  const CURRENCY_SYMBOLS: Record<string, string> = {
    GBP: '£',
    USD: '$',
    EUR: '€',
    AUD: 'A$',
    CAD: 'C$',
    NZD: 'NZ$',
    JPY: '¥'
  }

  const handleCopyToClipboard = async (text: string) => {
    const value = typeof text === 'string' ? text : String(text ?? '')

    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value)
      updateButtonLabelToCopied()

      return true
    }

    const ta = document.createElement('textarea')
    ta.value = value
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()

    let ok = false
    try {
      ok = document.execCommand('copy')
    } catch {
      ok = false
    }
    document.body.removeChild(ta)

    if (!ok) {
      updateButtonLabelToCopied()
    }

    return ok
  }

  const updateButtonLabelToCopied = () => {
    setCopied(true)
    setTimeout(() => {
      setCopied(false)
    }, 4000)
  }

  const handleClose = () => {
    setAmount(null)
    setClient(null)
    setPaymentLink(null)
    setPlaidBankAccount(null)
    setReference('')
    setOpen(false)
  }

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (!plaidBankAccount || !amount || !reference || testData) return

    if (amount < MIN_AMOUNT) return

    if (reference.length > 18) return

    try {
      const response = await createPaymentLink({
        plaidBankAccountId: plaidBankAccount.plaidBankAccountId,
        value: amount,
        paymentReference: reference,
        ledgerPostingId: unpaidBills ? unpaidBills.ledgerPostingId : null
      })

      if (response) {
        setPaymentLink(response)
      }
    } catch (error: any) {
      console.log('Error creating payment link:', error)
    }
  }

  useEffect(() => {
    const computed = unpaidBills ? Number(unpaidBills.officeAmount - unpaidBills.sumAllocationsAmount) : null

    setAmount(clampAmount(computed)) // <— enforce ≥ 1.00
    setReference(
      unpaidBills
        ? `${String(unpaidBills.clientNo ?? '')}${String(unpaidBills.matterNo ?? '')}${String(unpaidBills.postingReference ?? '')}`
        : ''
    )
  }, [unpaidBills])

  useEffect(() => {
    const ok = !!plaidBankAccount && !!reference && typeof amount === 'number' && amount >= MIN_AMOUNT

    setSaveDisabled(!ok)
  }, [plaidBankAccount, amount, reference])

  useEffect(() => {
    const getCurrencySymbol = (iso?: string) => {
      const fallback = '£'
      if (!iso) {
        setCurrencySymbol(fallback)
        return
      }
      const key = iso.toUpperCase()

      if (CURRENCY_SYMBOLS[key]) {
        setCurrencySymbol(CURRENCY_SYMBOLS[key])
        return
      }

      try {
        const parts = new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: key,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).formatToParts(0)

        const sym = parts.find((p) => p.type === 'currency')?.value
        setCurrencySymbol(sym || key)
      } catch {
        setCurrencySymbol(key)
      }
    }

    const iso = plaidBankAccount?.isoCurrencyCode
    getCurrencySymbol(iso)
  }, [plaidBankAccount?.isoCurrencyCode])

  useEffect(() => {
    if (client) setUnpaidBills(null)
  }, [client])

  useEffect(() => {
    setAmount(unpaidBills ? Number(unpaidBills.officeAmount - unpaidBills.sumAllocationsAmount) : null)
    setReference(unpaidBills ? `${String(unpaidBills.clientNo ?? '')}` : '')
  }, [unpaidBills])

  useEffect(() => {
    if (!bankFeed) return

    setPlaidBankAccount(bankFeed)
  }, [bankFeed, plaidBankAccount])

  return (
    <>
      <MUIPaper
        data-testid="BankFeedsCreatePaymentLink"
        elevation={20}
        sx={{
          height: 'fit-content',
          margin: '20px',
          minHeight: '450px',
          maxWidth: '600px',
          opacity: isOpen ? 1 : 0,
          top: isOpen ? '20%' : '0',
          left: isOpen ? 'calc(50% - 300px)' : '0',
          pb: '20px',
          pointerEvents: isOpen ? 'auto' : 'none',
          position: 'fixed',
          visibility: isOpen ? 'visible' : 'hidden',
          width: '100%',
          zIndex: 910
        }}
      >
        <MUIStack direction="row" justifyContent="space-between" alignItems="center" sx={{ m: '1.5rem 1.5rem 1rem' }}>
          <MUITypography variant="h4" sx={{ m: 0 }}>
            Create payment link
          </MUITypography>
          <MUIStack direction="row" alignItems="center" gap={1}>
            <MUIButton
              onClick={handleClose}
              variant="text"
              sx={{ minWidth: 16, height: 16, width: 16, p: 0, color: theme.palette.primary.main }}
            >
              <CloseFilledIcon
                sx={{
                  width: '1.5rem',
                  height: '1.5rem',
                  cursor: 'pointer',
                  color: theme.palette.primary.main,
                  opacity: 0.6
                }}
              />
            </MUIButton>
          </MUIStack>
        </MUIStack>
        <hr
          style={{
            width: 'calc(100% - 3rem)',
            margin: '0 1.5rem',
            padding: 0,
            border: '1px solid rgba(5, 28, 51, 0.06)'
          }}
        />

        {!paymentLink || paymentLink === null || paymentLink === '' ? (
          <form onSubmit={handleSave}>
            <MUIStack
              direction="column"
              gap={4}
              justifyContent={'center'}
              alignItems={'center'}
              sx={{ mt: '2rem', mb: '1rem', mx: 'auto', maxWidth: '400px' }}
            >
              <PlaidBankAccountSelect
                label="Receiving bank"
                value={plaidBankAccount}
                onChange={setPlaidBankAccount}
                size="medium"
                required
              />
              <ClientSelect
                size="medium"
                value={client}
                onChange={setClient}
                name="client"
                label="Client"
                sx={{ '& > div': { width: '100%', left: 0 } }}
              />
              <UnpaidBillsSelect
                size="medium"
                value={unpaidBills}
                onChange={setUnpaidBills}
                label="Associated bill"
                sx={{ '& > div': { width: '100%', left: 0 } }}
                clientNo={client?.clientNo}
                disabled={!client}
              />
              <NumericFormat
                value={amount ?? ''}
                onValueChange={({ floatValue }: any) => setAmount(floatValue ?? null)}
                isAllowed={({ value, floatValue }: any) => value === '' || (floatValue ?? 0) >= MIN_AMOUNT}
                onBlur={() => setAmount((prev) => clampAmount(prev as number | null))}
                customInput={MUITextField}
                label="Amount"
                variant="outlined"
                required
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                thousandSeparator={false}
                inputMode="decimal"
                InputProps={{
                  startAdornment: (
                    <MUIInputAdornment position="start" sx={{ '& .MuiTypography-root': { mb: 0 } }}>
                      {currencySymbol}
                    </MUIInputAdornment>
                  )
                }}
                sx={{
                  backgroundColor: theme.palette.primary.contrastText,
                  width: '100%',
                  maxWidth: '400px',
                  '& .MuiInputBase-root': { mb: 0 },
                  "& > label[data-shrink='true']": { top: 0, m: 0 }
                }}
              />

              <MUITextField
                label="Reference"
                variant="outlined"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                required
                inputProps={{ maxLength: 18, pattern: '[A-Za-z0-9]*' }}
                helperText="Letters and numbers only, max 18 characters"
                sx={{
                  width: '100%',
                  maxWidth: '400px',
                  mb: 1,
                  '& .MuiInputBase-root': { mb: 0, background: 'white' },
                  "& > label[data-shrink='true']": { top: 0, m: 0 },
                  '.MuiFormHelperText-root': { background: 'transparent' }
                }}
              />
            </MUIStack>
            <MUIStack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              sx={{ mt: '2rem', mr: '2rem', gap: '1rem' }}
            >
              <MUIButton onClick={handleClose} variant="text">
                Cancel
              </MUIButton>
              <MUIButton
                variant="contained"
                type="submit"
                disabled={saveDisabled}
                aria-label={!saveDisabled ? '' : 'Please fill in all required fields before saving'}
              >
                Submit
              </MUIButton>
            </MUIStack>
          </form>
        ) : (
          <>
            <MUIStack
              direction="column"
              alignItems="flex-start"
              sx={{
                my: '3rem',
                mx: '5rem',
                gap: '1rem',
                backgroundColor: 'rgba(100, 116, 149, 0.1)',
                p: '2rem'
              }}
            >
              <MUITypography variant="h5" sx={{ color: theme.palette.primary.light }}>
                Your payment link:
              </MUITypography>
              <MUITypography
                variant="body1"
                sx={{
                  wordBreak: 'break-all',
                  textAlign: 'left',
                  color: theme.palette.primary.light
                }}
              >
                {paymentLink}
              </MUITypography>
            </MUIStack>
            <MUIStack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              sx={{ mt: '2rem', mr: '2rem', gap: '1rem' }}
            >
              <MUIButton
                onClick={() => handleCopyToClipboard(paymentLink)}
                disabled={copied}
                variant="contained"
                sx={{ color: theme.palette.primary.contrastText, marginBottom: '20px' }}
              >
                {!copied && (
                  <CopyOutlineIcon
                    sx={{ height: 16, width: 16, fill: theme.palette.primary.contrastText, mr: '0.5rem' }}
                  />
                )}
                {copied ? `Copied!` : `Copy to clipboard`}
              </MUIButton>
            </MUIStack>
          </>
        )}
      </MUIPaper>

      <Portal>
        <MUIBackdrop open={isOpen} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 900 }} />
      </Portal>
    </>
  )
}

type BankFeedsCreatePaymentLinkProps = {
  bankFeed?: any
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
  testData?: boolean
}
